import React from 'react';
import { ShoppingBagIcon, UsersIcon, GlobeIcon, TrendingUpIcon, ShieldCheckIcon } from 'lucide-react';
import CTAButton from '../components/ui/CTAButton';
const AboutPage = () => {
  // Team members
  const teamMembers = [{
    name: 'Sarah Johnson',
    role: 'Founder & CEO',
    image: 'https://randomuser.me/api/portraits/women/23.jpg',
    bio: "Sarah founded MarketHub with a vision to create a trusted marketplace where independent sellers could thrive. With 15+ years in e-commerce, she leads the company's strategic direction."
  }, {
    name: 'Michael Chen',
    role: 'CTO',
    image: 'https://randomuser.me/api/portraits/men/54.jpg',
    bio: "Michael oversees MarketHub's technology infrastructure and product development. His expertise in scalable platforms ensures a seamless experience for buyers and sellers."
  }, {
    name: 'Aisha Patel',
    role: 'Head of Seller Success',
    image: 'https://randomuser.me/api/portraits/women/37.jpg',
    bio: "Aisha and her team work directly with sellers to help them grow their businesses. She's passionate about empowering entrepreneurs with the tools they need to succeed."
  }, {
    name: 'David Rodriguez',
    role: 'Customer Experience Director',
    image: 'https://randomuser.me/api/portraits/men/29.jpg',
    bio: 'David ensures that every customer interaction with MarketHub is exceptional. He leads our support team and constantly improves our customer service processes.'
  }];
  // Company stats
  const stats = [{
    value: '10K+',
    label: 'Verified Sellers'
  }, {
    value: '2M+',
    label: 'Happy Customers'
  }, {
    value: '150K+',
    label: 'Products Available'
  }, {
    value: '120+',
    label: 'Countries Served'
  }];
  return <div className="bg-gray-50 min-h-screen" data-id="element-272">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-700 to-blue-900 text-white py-20" data-id="element-273">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1556745753-b2904692b3cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1373&q=80')"
      }} data-id="element-274"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-275">
          <div className="max-w-3xl" data-id="element-276">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-277">
              About MarketHub
            </h1>
            <p className="text-xl md:text-2xl" data-id="element-278">
              Connecting quality sellers with happy customers since 2018. We're
              on a mission to create the most trusted marketplace for unique
              products.
            </p>
          </div>
        </div>
      </section>
      {/* Our Story */}
      <section className="py-16" data-id="element-279">
        <div className="container mx-auto px-4" data-id="element-280">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-281">
            <div data-id="element-282">
              <h2 className="text-3xl font-bold mb-6" data-id="element-283">Our Story</h2>
              <p className="text-gray-700 mb-4" data-id="element-284">
                MarketHub began with a simple idea: create a platform where
                independent sellers could easily connect with customers looking
                for unique, quality products. Our founder, Sarah Johnson, saw
                how difficult it was for small businesses to gain visibility
                online and decided to build a solution.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-285">
                Starting with just 50 sellers in 2018, we've grown into a global
                marketplace with thousands of verified merchants offering
                everything from handcrafted jewelry to vintage home decor and
                cutting-edge electronics.
              </p>
              <p className="text-gray-700" data-id="element-286">
                What sets us apart is our commitment to quality, trust, and
                community. We carefully vet all sellers, ensure secure
                transactions, and provide exceptional support to both buyers and
                sellers. Our platform isn't just a place to buy and sell—it's a
                community where entrepreneurs can thrive and customers can
                discover products they love.
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-287">
              <img src="https://images.unsplash.com/photo-1556745757-8d76bdb6984b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1373&q=80" alt="MarketHub team meeting" className="w-full h-full object-cover" data-id="element-288" />
            </div>
          </div>
        </div>
      </section>
      {/* Our Values */}
      <section className="py-16 bg-white" data-id="element-289">
        <div className="container mx-auto px-4" data-id="element-290">
          <div className="text-center mb-16" data-id="element-291">
            <h2 className="text-3xl font-bold mb-4" data-id="element-292">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-293">
              These core principles guide everything we do at MarketHub
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-294">
            <div className="bg-gray-50 p-8 rounded-xl" data-id="element-295">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-296">
                <ShieldCheckIcon className="h-8 w-8 text-blue-600" data-id="element-297" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-298">Trust & Quality</h3>
              <p className="text-gray-600" data-id="element-299">
                We verify all sellers and maintain high product standards to
                ensure every customer has a positive experience. Trust is the
                foundation of our marketplace.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl" data-id="element-300">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-301">
                <UsersIcon className="h-8 w-8 text-blue-600" data-id="element-302" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-303">Community First</h3>
              <p className="text-gray-600" data-id="element-304">
                We believe in building meaningful connections between sellers
                and buyers. Our platform fosters a community where everyone can
                grow and succeed together.
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl" data-id="element-305">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-6" data-id="element-306">
                <TrendingUpIcon className="h-8 w-8 text-blue-600" data-id="element-307" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-308">
                Empowering Entrepreneurs
              </h3>
              <p className="text-gray-600" data-id="element-309">
                We're dedicated to providing sellers with the tools, resources,
                and support they need to build successful businesses and reach
                new customers.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-16 bg-blue-600 text-white" data-id="element-310">
        <div className="container mx-auto px-4" data-id="element-311">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-312">
            {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-313">
                <div className="text-4xl md:text-5xl font-bold mb-2" data-id="element-314">
                  {stat.value}
                </div>
                <div className="text-blue-100" data-id="element-315">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>
      {/* Our Team */}
      <section className="py-16" data-id="element-316">
        <div className="container mx-auto px-4" data-id="element-317">
          <div className="text-center mb-16" data-id="element-318">
            <h2 className="text-3xl font-bold mb-4" data-id="element-319">Meet Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-320">
              The passionate people behind MarketHub who work every day to make
              our marketplace better
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-321">
            {teamMembers.map((member, index) => <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-322">
                <img src={member.image} alt={member.name} className="w-full h-64 object-cover" data-id="element-323" />
                <div className="p-6" data-id="element-324">
                  <h3 className="font-semibold text-lg mb-1" data-id="element-325">{member.name}</h3>
                  <p className="text-blue-600 mb-3" data-id="element-326">{member.role}</p>
                  <p className="text-gray-600 text-sm" data-id="element-327">{member.bio}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      {/* Global Reach */}
      <section className="py-16 bg-white" data-id="element-328">
        <div className="container mx-auto px-4" data-id="element-329">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-330">
            <div className="order-2 lg:order-1" data-id="element-331">
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-332">
                <img src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5ce?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1333&q=80" alt="Global marketplace" className="w-full h-full object-cover" data-id="element-333" />
              </div>
            </div>
            <div className="order-1 lg:order-2" data-id="element-334">
              <div className="flex items-center mb-4" data-id="element-335">
                <GlobeIcon className="h-8 w-8 text-blue-600 mr-3" data-id="element-336" />
                <h2 className="text-3xl font-bold" data-id="element-337">
                  Global Reach, Local Touch
                </h2>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-338">
                MarketHub connects sellers and buyers from over 120 countries,
                creating a truly global marketplace. We celebrate the diversity
                of products and cultures represented on our platform.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-339">
                While we operate globally, we maintain a local touch by
                supporting regional sellers, understanding local market needs,
                and providing localized customer support in multiple languages.
              </p>
              <p className="text-gray-700" data-id="element-340">
                Our international shipping network ensures that unique products
                can reach customers anywhere in the world, while our secure
                payment system handles multiple currencies and payment methods.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Join Us CTA */}
      <section className="py-16 bg-gray-50" data-id="element-341">
        <div className="container mx-auto px-4 text-center" data-id="element-342">
          <h2 className="text-3xl font-bold mb-4" data-id="element-343">
            Join the MarketHub Community
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" data-id="element-344">
            Whether you're looking to shop unique products or start selling your
            own, MarketHub is the trusted platform for you.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-345">
            <CTAButton to="/categories" text="Start Shopping" variant="primary" size="lg" data-id="element-346" />
            <CTAButton to="/become-seller" text="Become a Seller" variant="outline" size="lg" data-id="element-347" />
          </div>
        </div>
      </section>
    </div>;
};
export default AboutPage;